import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  organizationWebhooksService,
  type OrganizationWebhookConfig,
  type OrganizationWebhookHandshakeResult,
  type OrganizationWebhookTestResult,
} from "../services/organizationWebhooksService";

export interface OrganizationWebhookConfigData {
  /** The endpoint, `null` once loaded when the organization has none yet. */
  config: OrganizationWebhookConfig | null;
  /** True until the first read settles — the section renders a skeleton, not the empty state, meanwhile. */
  isLoading: boolean;
  /** A 401/403 on the read: the whole section hides (backend-authoritative, like the access sections). */
  hasNoAccess: boolean;
  /** A non-access failure on the read; the section offers a retry. */
  hasError: boolean;
  reload: () => Promise<void>;

  /** True while any mutation is in flight — dialogs block closing and double-submits on it. */
  isSubmitting: boolean;
  /**
   * The plaintext secret the backend just set (generated on create, or replaced/rotated). It exists on screen
   * only until `dismissSecret` — there is no endpoint to read it back.
   */
  revealedSecret: string | null;
  dismissSecret: () => void;
  /**
   * The live secret, read back on an explicit gesture through the audited reveal endpoint. Distinct from
   * {@link revealedSecret}, which is the just-issued value the reader has to store; this one they can look up
   * again whenever they need it.
   */
  fullSecret: string | null;
  isRevealingSecret: boolean;
  revealSecret: () => Promise<void>;
  hideFullSecret: () => void;
  /** Outcome of the last connection test, shown inline until the next one or a config change. */
  testResult: OrganizationWebhookTestResult | null;
  clearTestResult: () => void;

  /**
   * Registers the endpoint (first time) or re-points an existing one.
   *
   * On a first registration this runs the configuration handshake client-side: the endpoint is saved
   * **paused**, a `test` / `ping` is sent, and deliveries are enabled only if the receiver echoed the secret.
   * That way a typo never puts real events on the floor.
   *
   * On an update the gate is the BACKEND's: changing the URL or the secret invalidates the proof the endpoint
   * was verified with, so the change lands paused and the backend re-runs the handshake itself, switching
   * deliveries back on only if the receiver answers correctly. The verdict comes back as `revalidation`.
   */
  saveEndpoint: (input: { url: string; secret?: string }) => Promise<EndpointSaveOutcome>;
  /**
   * Pause / resume deliveries, and what re-enables an endpoint the backend disabled. Activation is not a
   * write the backend takes on trust: it re-runs the handshake live and refuses with a 400 if the receiver
   * does not echo the secret, leaving the endpoint untouched. So this can fail for a reason that is about the
   * customer's server, not their permissions.
   */
  setActive: (isActive: boolean) => Promise<boolean>;
  /**
   * Issues a new secret. The backend then re-verifies with it, which a receiver that has not been updated yet
   * cannot pass — so the normal outcome is a paused endpoint, and the toast says which one happened.
   */
  rotateSecret: () => Promise<boolean>;
  removeEndpoint: () => Promise<boolean>;
  /** Synchronous ping. Resolves the result, or null when the request itself failed. */
  sendTest: () => Promise<OrganizationWebhookTestResult | null>;
}

/** What `saveEndpoint` did, so the dialog knows whether to close and what to say. */
export interface EndpointSaveOutcome {
  /** False when the request failed outright — the error toast already fired. */
  saved: boolean;
  /** Present on a first registration: what the receiver answered to the handshake ping. */
  handshake?: OrganizationWebhookTestResult;
  /**
   * Present on an update that invalidated the endpoint's proof: the verdict of the re-verification the
   * backend ran. Anything but `verified` means the endpoint is now paused.
   */
  revalidation?: OrganizationWebhookHandshakeResult;
  /** True when the handshake succeeded and deliveries were enabled. */
  activated?: boolean;
}

/**
 * The organization's webhook endpoint: one read, every mutation, and the two pieces of transient state the
 * section needs (the one-time secret and the last test result). Instantiated once by
 * `OrganizationWebhooksProvider` so the page's section visibility and the section body share the read.
 */
export function useOrganizationWebhookConfig(): OrganizationWebhookConfigData {
  const { t } = useTranslations("settings");

  const [config, setConfig] = useState<OrganizationWebhookConfig | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [settledKey, setSettledKey] = useState<number | null>(null);
  const isLoading = settledKey !== reloadKey && !hasLoadedOnce;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [fullSecret, setFullSecret] = useState<string | null>(null);
  const [isRevealingSecret, setIsRevealingSecret] = useState(false);
  const [testResult, setTestResult] = useState<OrganizationWebhookTestResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    organizationWebhooksService
      .getConfig()
      .then((loaded) => {
        if (cancelled) return;
        setConfig(loaded);
        setHasError(false);
        setHasLoadedOnce(true);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setHasNoAccess(true);
        else setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setSettledKey(reloadKey);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const reload = useCallback(async () => {
    setReloadKey((key) => key + 1);
  }, []);

  /** Runs one mutation with the shared in-flight flag and the shared error toast. */
  const run = useCallback(
    async <T>(mutation: () => Promise<T>): Promise<T | null> => {
      setIsSubmitting(true);
      try {
        return await mutation();
      } catch (error: unknown) {
        colocaToast.error(
          HttpApiService.isAccessDeniedError(error)
            ? t("webhooks_error_no_write_permission")
            : HttpApiService.getErrorMessage(error),
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [t],
  );

  const saveEndpoint = useCallback(
    async ({ url, secret }: { url: string; secret?: string }): Promise<EndpointSaveOutcome> => {
      const isFirstRegistration = config === null;

      const saved = await run(() =>
        organizationWebhooksService.upsertConfig({
          url,
          secret,
          // First registration lands paused: nothing is delivered until the handshake below proves the
          // receiver answers. An update states the state the operator wants — the backend pauses it anyway
          // while it re-verifies, and honours this only once the receiver has echoed the secret.
          is_active: isFirstRegistration ? false : config.is_active,
        }),
      );
      if (!saved) return { saved: false };

      const { secret: issuedSecret, handshake: revalidation, ...rest } = saved;
      setConfig(rest);
      // Present when the secret was just set — generated on create, or replaced by this call.
      if (issuedSecret) setRevealedSecret(issuedSecret);
      setTestResult(null);

      if (!isFirstRegistration) {
        // `revalidation` is only present when the change invalidated the endpoint's proof. Reporting a bare
        // "saved" then would hide the thing the operator most needs to know: deliveries have stopped.
        if (revalidation && revalidation !== "verified") {
          colocaToast.warning(t("webhooks_toast_saved_revalidation_failed"));
        } else if (revalidation === "verified" && rest.is_active) {
          colocaToast.success(t("webhooks_toast_handshake_ok"));
        } else {
          colocaToast.success(t("webhooks_toast_saved"));
        }
        return { saved: true, revalidation };
      }

      // The configuration handshake. A failure here is not an error: the endpoint is registered and paused,
      // and the reader gets the receiver's own answer to work from.
      const handshake = await run(() => organizationWebhooksService.testConnection());
      if (!handshake) {
        colocaToast.success(t("webhooks_toast_saved_paused"));
        return { saved: true };
      }
      setTestResult(handshake);

      if (!handshake.success) {
        colocaToast.warning(t("webhooks_toast_handshake_failed"));
        return { saved: true, handshake, activated: false };
      }

      const activated = await run(() => organizationWebhooksService.upsertConfig({ url, is_active: true }));
      if (activated) {
        const { secret: _ignored, ...activatedConfig } = activated;
        setConfig(activatedConfig);
      }
      colocaToast.success(t("webhooks_toast_handshake_ok"));
      return { saved: true, handshake, activated: activated !== null };
    },
    [config, run, t],
  );

  const setActive = useCallback(
    async (isActive: boolean): Promise<boolean> => {
      if (!config) return false;
      const saved = await run(() =>
        organizationWebhooksService.upsertConfig({ url: config.url, is_active: isActive }),
      );
      // A refused activation is checked before anything is written, so the endpoint is exactly as it was and
      // there is nothing to reload. The backend's own message names the handshake result, which is the only
      // thing that tells the operator what to fix on their server.
      if (!saved) return false;
      const { secret: _secret, ...rest } = saved;
      setConfig(rest);
      colocaToast.success(t(isActive ? "webhooks_toast_resumed" : "webhooks_toast_paused"));
      return true;
    },
    [config, run, t],
  );

  const revealSecret = useCallback(async (): Promise<void> => {
    setIsRevealingSecret(true);
    try {
      const revealed = await organizationWebhooksService.revealSecret();
      setFullSecret(revealed.secret);
    } catch (error: unknown) {
      colocaToast.error(
        HttpApiService.isAccessDeniedError(error)
          ? t("webhooks_error_no_reveal_permission")
          : HttpApiService.getErrorMessage(error),
      );
    } finally {
      setIsRevealingSecret(false);
    }
  }, [t]);

  const rotateSecret = useCallback(async (): Promise<boolean> => {
    const rotated = await run(() => organizationWebhooksService.rotateSecret());
    if (!rotated) return false;
    const { secret, handshake, ...rest } = rotated;
    setConfig(rest);
    setRevealedSecret(secret ?? null);
    // Whatever was on screen is now the previous secret.
    setFullSecret(null);
    // The backend re-verifies with the NEW secret, which a receiver that has not been updated yet cannot
    // echo — so a paused endpoint is the EXPECTED outcome of a rotation, not an error. Saying "rotated" and
    // nothing else would leave the operator to discover the stop from the health strip.
    if (handshake && handshake !== "verified") {
      colocaToast.warning(t("webhooks_toast_rotated_paused"));
    } else {
      colocaToast.success(t("webhooks_toast_rotated"));
    }
    return true;
  }, [run, t]);

  const removeEndpoint = useCallback(async (): Promise<boolean> => {
    const done = await run(async () => {
      await organizationWebhooksService.deleteConfig();
      return true;
    });
    if (!done) return false;
    setConfig(null);
    setRevealedSecret(null);
    setTestResult(null);
    colocaToast.success(t("webhooks_toast_deleted"));
    return true;
  }, [run, t]);

  const sendTest = useCallback(async (): Promise<OrganizationWebhookTestResult | null> => {
    const result = await run(() => organizationWebhooksService.testConnection());
    if (result) setTestResult(result);
    return result;
  }, [run]);

  return useMemo(
    () => ({
      config,
      isLoading,
      hasNoAccess,
      hasError,
      reload,
      isSubmitting,
      revealedSecret,
      dismissSecret: () => setRevealedSecret(null),
      fullSecret,
      isRevealingSecret,
      revealSecret,
      hideFullSecret: () => setFullSecret(null),
      testResult,
      clearTestResult: () => setTestResult(null),
      saveEndpoint,
      setActive,
      rotateSecret,
      removeEndpoint,
      sendTest,
    }),
    [
      config,
      isLoading,
      hasNoAccess,
      hasError,
      reload,
      isSubmitting,
      revealedSecret,
      fullSecret,
      isRevealingSecret,
      revealSecret,
      testResult,
      saveEndpoint,
      setActive,
      rotateSecret,
      removeEndpoint,
      sendTest,
    ],
  );
}
